// "Producteurs" tab — searchable producer table with expandable rows.
// Each row's expansion lists the producer's parcels with risk
// indicators. Mirrors the legacy ProducerList but trimmed of the
// per-row photo (we don't have one on v2's Producer model).

import { useMemo, useState } from "react"
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  MapPin,
  Search,
  XCircle,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import type {
  OTTraceability,
  TraceabilityParcelEntry,
  TraceabilityProducerEntry,
} from "@/features/transit/api/types"

interface Props {
  traceability: OTTraceability
}

export function TraceabilityProducersTab({ traceability }: Props) {
  const { t } = useTranslation("transit")
  const [search, setSearch] = useState("")
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const parcelsByFarmer = useMemo(() => {
    const out = new Map<number, TraceabilityParcelEntry[]>()
    for (const parcel of traceability.parcels) {
      const list = out.get(parcel.farmerId) ?? []
      list.push(parcel)
      out.set(parcel.farmerId, list)
    }
    return out
  }, [traceability.parcels])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return traceability.producers
    return traceability.producers.filter((p) => {
      return (
        p.name.toLowerCase().includes(term) ||
        (p.internalFarmerId ?? "").toLowerCase().includes(term) ||
        (p.rhizoFarmerId ?? "").toLowerCase().includes(term)
      )
    })
  }, [search, traceability.producers])

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("trace.producers.searchPlaceholder")}
          className="pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-muted-foreground">
          {search
            ? t("trace.producers.searchEmpty")
            : t("trace.producers.empty")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.producers.col.id")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.producers.col.name")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.producers.col.certifications")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("trace.producers.col.parcels")}
                </th>
                <th className="px-3 py-2 text-left font-semibold">
                  {t("trace.producers.col.eudr")}
                </th>
                <th className="w-10 px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((producer) => {
                const isOpen = expandedId === producer.id
                const parcels = parcelsByFarmer.get(producer.id) ?? []
                return (
                  <Row
                    key={producer.id}
                    producer={producer}
                    parcels={parcels}
                    isOpen={isOpen}
                    onToggle={() =>
                      setExpandedId(isOpen ? null : producer.id)
                    }
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function Row({
  producer,
  parcels,
  isOpen,
  onToggle,
}: {
  producer: TraceabilityProducerEntry
  parcels: TraceabilityParcelEntry[]
  isOpen: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation("transit")
  return (
    <>
      <tr
        className="cursor-pointer hover:bg-muted/40"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <td className="px-3 py-2 font-medium text-foreground">
          {producer.rhizoFarmerId ??
            producer.internalFarmerId ??
            `#${producer.id}`}
        </td>
        <td className="px-3 py-2">{producer.name}</td>
        <td className="px-3 py-2 text-muted-foreground">
          {producer.certificates.join(", ") || "—"}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {producer.parcelsCount}
        </td>
        <td className="px-3 py-2">
          <EUDRPill compliant={producer.isEUDR} />
        </td>
        <td className="px-3 py-2 text-right text-muted-foreground">
          {isOpen ? (
            <ChevronUp className="size-4" />
          ) : (
            <ChevronDown className="size-4" />
          )}
        </td>
      </tr>
      {isOpen ? (
        <tr className="bg-muted/30">
          <td colSpan={6} className="px-3 py-3">
            {parcels.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("trace.producers.parcelsEmpty")}
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {parcels.map((parcel) => (
                  <li
                    key={parcel.id}
                    className="flex items-start gap-2 rounded-md border bg-card px-3 py-2"
                  >
                    <MapPin className="mt-0.5 size-4 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {parcel.parcelId ??
                          parcel.internalId ??
                          `#${parcel.id}`}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {parcel.hectares.toFixed(2)} ha · {parcel.status}
                      </div>
                    </div>
                    <ParcelStatusChip parcel={parcel} />
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      ) : null}
    </>
  )
}

function EUDRPill({ compliant }: { compliant: boolean }) {
  const { t } = useTranslation("transit")
  if (compliant) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
        <CheckCircle2 className="size-3" />
        {t("trace.producers.eudrOk")}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
      <XCircle className="size-3" />
      {t("trace.producers.eudrKo")}
    </span>
  )
}

function ParcelStatusChip({ parcel }: { parcel: TraceabilityParcelEntry }) {
  const { t } = useTranslation("transit")
  if (parcel.isCompliant) {
    return (
      <span className="inline-flex flex-shrink-0 items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
        {t("trace.parcels.statusOk")}
      </span>
    )
  }
  return (
    <span className="inline-flex flex-shrink-0 items-center rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
      {t("trace.parcels.statusRisk")}
    </span>
  )
}
